/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/usePullToRefresh.ts
 *
 * Rôle :
 *   Pull-to-refresh sur PWA installée (iOS standalone + Android
 *   standalone), geste natif du smartphone : tirer vers le bas
 *   depuis le haut de la page → rechargement de l'app.
 *
 *   Pourquoi un hook custom plutôt que le natif navigateur ?
 *   - Sur Safari iOS PWA `display:standalone` : Apple DÉSACTIVE le
 *     pull-to-refresh natif. Pas de remède côté config.
 *   - Sur Chrome Android PWA `display:standalone` : idem, désactivé
 *     par Google.
 *   - Sur les onglets navigateur classiques : marche nativement,
 *     notre hook ne nuit PAS (il détecte uniquement le geste long
 *     depuis le HAUT de la page, en parallèle du natif).
 *
 *   Demande Mika 2026-05-26 (J-1) : « lorsque je tire l'application
 *   vers le bas sur smartphone, ça devrait forcer l'actualiser de
 *   l'application. ».
 *
 * Détection :
 *   1. touchstart avec `window.scrollY === 0` (utilisateur en haut)
 *      ET 1 seul doigt (sinon = pinch sur /carte, on ne touche pas).
 *   2. touchmove : si delta vertical > 0 (vers le bas), on stocke
 *      l'amplitude. preventDefault SEULEMENT au-delà du seuil minimal
 *      pour ne pas casser le scroll natif inverse.
 *   3. touchend : si amplitude >= REFRESH_THRESHOLD → trigger refresh
 *      (callback fourni par le composant overlay = window.location.reload).
 *      Sinon snap back silencieusement.
 *
 * Garde-fous :
 *   - 1 seul doigt strict (compatible pinch-zoom /carte).
 *   - scrollY === 0 strict (compatible scroll pages longues).
 *   - Désactivable si modal ouvert (état `enabled`).
 *   - `{ passive: false }` pour pouvoir preventDefault sur iOS.
 *
 * Réutilisable :
 *   Hook 100 % générique. À monter dans AppLayout pour couvrir
 *   toute l'app. NOLAS Tracking pourrait l'utiliser tel quel.
 *
 * @author Mika
 * @since 2026-05-26
 */

import { useEffect, useRef, useState } from 'react';

/** Seuil minimal de pull (px) avant d'afficher l'indicateur visuel. */
const PULL_VISIBLE_THRESHOLD = 20;
/** Seuil de déclenchement du refresh (px). 80 = bon compromis tactile. */
export const REFRESH_THRESHOLD = 80;
/** Amplitude maximale rendue à l'écran (px), au-delà = elastic damping. */
const MAX_PULL = 150;

export interface PullToRefreshState {
    /** Distance pull actuelle (px), 0 si pas de pull en cours. */
    pull: number;
    /** true quand l'utilisateur a passé le seuil de refresh. */
    canRefresh: boolean;
    /** true le temps que le reload se déclenche (animation). */
    refreshing: boolean;
}

export interface UsePullToRefreshOptions {
    /** Désactiver le hook (ex. quand un modal est ouvert). */
    enabled?: boolean;
    /** Callback de refresh, défaut window.location.reload(). */
    onRefresh?: () => void;
}

/**
 * Hook pull-to-refresh.
 *
 * Retourne l'état pour qu'un composant `<PullToRefreshOverlay>` puisse
 * afficher l'indicateur visuel (spinner + flèche).
 *
 * Le geste est attaché au `document.body` (capture phase) pour ne pas
 * interférer avec les listeners locaux (ex. pinch-zoom /carte sur
 * son svgContainerRef).
 */
export function usePullToRefresh(
    options: UsePullToRefreshOptions = {},
): PullToRefreshState {
    const { enabled = true, onRefresh } = options;
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const startYRef = useRef<number | null>(null);
    const trackingRef = useRef<boolean>(false);

    useEffect(() => {
        if (!enabled) {
            setPull(0);
            setRefreshing(false);
            return;
        }

        const onTouchStart = (e: TouchEvent): void => {
            // 1 seul doigt + page tout en haut (sinon ne pas interférer
            // avec pinch-zoom /carte ou scroll pages longues).
            if (e.touches.length !== 1) {
                trackingRef.current = false;
                return;
            }
            if (window.scrollY > 0) {
                trackingRef.current = false;
                return;
            }
            const t0 = e.touches[0];
            if (!t0) return;
            startYRef.current = t0.clientY;
            trackingRef.current = true;
        };

        const onTouchMove = (e: TouchEvent): void => {
            if (!trackingRef.current || startYRef.current === null) return;
            if (e.touches.length !== 1) {
                trackingRef.current = false;
                setPull(0);
                return;
            }
            const t0 = e.touches[0];
            if (!t0) return;
            const delta = t0.clientY - startYRef.current;
            if (delta <= 0) {
                // L'utilisateur tire vers le haut → c'est du scroll
                // normal, on ne fait rien.
                trackingRef.current = false;
                setPull(0);
                return;
            }
            // preventDefault au-delà du seuil visible, empêche le
            // « bounce » iOS et le rubber-band Android qui perturbent
            // notre overlay.
            if (delta > PULL_VISIBLE_THRESHOLD) {
                try { e.preventDefault(); } catch { /* noop */ }
            }
            // Elastic damping au-delà de MAX_PULL (résistance croissante).
            const capped = delta > MAX_PULL
                ? MAX_PULL + (delta - MAX_PULL) * 0.3
                : delta;
            setPull(capped);
        };

        const onTouchEnd = (): void => {
            if (!trackingRef.current) return;
            trackingRef.current = false;
            startYRef.current = null;
            setPull((current) => {
                if (current >= REFRESH_THRESHOLD) {
                    // Trigger refresh (avec mini-délai pour montrer l'anim).
                    setRefreshing(true);
                    setTimeout(() => {
                        if (onRefresh) {
                            onRefresh();
                        } else {
                            window.location.reload();
                        }
                    }, 300);
                    return current; // garde l'overlay visible
                }
                return 0;
            });
        };

        // Capture phase pour intercepter AVANT les listeners locaux
        // (ex. pinch sur /carte). Mais 1 doigt strict → pas de conflit.
        const opts: AddEventListenerOptions = { capture: false, passive: false };
        document.addEventListener('touchstart', onTouchStart, opts);
        document.addEventListener('touchmove', onTouchMove, opts);
        document.addEventListener('touchend', onTouchEnd, opts);
        document.addEventListener('touchcancel', onTouchEnd, opts);

        return () => {
            document.removeEventListener('touchstart', onTouchStart, opts);
            document.removeEventListener('touchmove', onTouchMove, opts);
            document.removeEventListener('touchend', onTouchEnd, opts);
            document.removeEventListener('touchcancel', onTouchEnd, opts);
        };
    }, [enabled, onRefresh]);

    return {
        pull,
        canRefresh: pull >= REFRESH_THRESHOLD,
        refreshing,
    };
}
